import { useEffect, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useCapabilities } from './CapabilityProvider';
import { getUnreadCount } from '../../services/adminNotificationsService';
import { createNotificationCountLoader } from '../../services/notificationCountLoader';
import Icon from './ui/icons/Icon';

export default function NotificationBell() {
  const navigate = useNavigate();
  const { has, me } = useCapabilities();
  const canRead = has('admin:audit:read') || has('admin:integrations:read') || has('admin:owner-dashboard:read');
  const actorId = me?.actorId || me?.id;
  const loaderRef = useRef(null);
  const [state, setState] = useState({ count: null, checkedAt: null, loading: false, error: false });

  useEffect(() => {
    setState({ count: null, checkedAt: null, loading: false, error: false });
    if (!canRead) return;
    const loader = createNotificationCountLoader(getUnreadCount, setState);
    loaderRef.current = loader;
    loader.refresh();
    const interval = setInterval(loader.refresh, 60000);
    return () => {
      clearInterval(interval);
      loader.dispose();
      if (loaderRef.current === loader) loaderRef.current = null;
    };
  }, [canRead, actorId]);

  if (!canRead) return null;
  const { count, checkedAt, loading, error } = state;
  const status = error
    ? count === null ? 'Unread count unavailable' : `${count} unread — last known count; update unavailable`
    : count === null ? 'Checking unread count' : `${count} unread`;
  const label = `Notifications: ${status}`;
  const title = `${label}${checkedAt ? `. Last checked ${new Date(checkedAt).toLocaleTimeString()}` : ''}`;

  return (
    <span style={{ display: 'inline-flex', alignItems: 'center' }}>
      <button className="admin-notification-button" onClick={() => navigate('/admin/notifications')}
        title={title} aria-label={label}>
        <Icon name="notifications" size="md" />
        {(count > 0 || error) && (
          <span className="admin-notification-badge" style={error ? { backgroundColor: '#92400e' } : undefined} aria-hidden="true">
            {error ? count === null ? '?' : `${count > 99 ? '99+' : count}!` : count > 99 ? '99+' : count}
          </span>
        )}
      </button>
      {error && (
        <button type="button" className="admin-notification-button" style={{ minWidth: 32, minHeight: 36, padding: 4, fontSize: 18 }}
          disabled={loading} onClick={() => loaderRef.current?.refresh()}
          title={loading ? 'Retrying unread count' : 'Retry unread count'}
          aria-label={loading ? 'Retrying unread count' : 'Retry unread count'}>
          {loading ? '…' : '↻'}
        </button>
      )}
      <span role="status" style={{ position: 'absolute', width: 1, height: 1, padding: 0, overflow: 'hidden', clipPath: 'inset(50%)', whiteSpace: 'nowrap' }}>
        {status}
      </span>
    </span>
  );
}
